// Guest lesson-visit tracker — ported from composable `hec/utils/guest-lesson-tracker.js`
// (SFCC-11505). Logged-out learners may view up to GUEST_LESSON_LIMIT (2)
// lessons before being prompted to sign in; visits are recorded in localStorage
// (separate from `guestLearningCenterData` progress, which mirrors the SFCC
// attribute). SSR-safe: every accessor no-ops to an empty list off the browser.
import { isBrowserStorageAvailable as isBrowser } from "~/lib/utils/safe-storage";

export const VISITED_LESSONS_KEY = "visitedLessons";
export const GUEST_LESSON_LIMIT = 2;

export interface VisitedLesson {
  lessonId: string;
  timeVisited: string; // ISO date
}

/** All recorded guest lesson visits (empty on SSR / absence / corruption). */
export function getVisitedLessons(): VisitedLesson[] {
  if (!isBrowser()) return [];
  try {
    const raw = localStorage.getItem(VISITED_LESSONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as VisitedLesson[]) : [];
  } catch {
    return [];
  }
}

/** True when this lesson has already been visited as a guest. */
export function isVisited(lessonId: string): boolean {
  return getVisitedLessons().some(entry => entry.lessonId === lessonId);
}

/**
 * Record a guest lesson visit. Idempotent (no duplicate entry) and capped at
 * GUEST_LESSON_LIMIT — once the cap is hit, further visits are NOT recorded
 * (the caller surfaces the limit toast). Returns the resulting list.
 */
export function trackLessonVisit(lessonId: string): VisitedLesson[] {
  const visited = getVisitedLessons();
  if (visited.some(entry => entry.lessonId === lessonId)) return visited;
  if (visited.length >= GUEST_LESSON_LIMIT) return visited;
  if (!isBrowser()) return visited;

  const updated = [
    ...visited,
    { lessonId, timeVisited: new Date().toISOString() },
  ];
  try {
    localStorage.setItem(VISITED_LESSONS_KEY, JSON.stringify(updated));
  } catch {
    /* ignore quota / serialization errors — tracking is best-effort */
  }
  return updated;
}

/**
 * Clear the guest visited-lessons cap. Called once on the guest→registered
 * merge so a freshly-registered user no longer carries the stale 2-lesson cap
 * (the cap is guest-only). SSR-safe and best-effort.
 *
 * Note: the composable also calls a sibling `clearCompletedLessons()` here, but
 * that helper is defined nowhere (a latent ReferenceError) — it is deliberately
 * NOT ported. Only the visited-lessons reset is real.
 */
export function clearVisitedLessons(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(VISITED_LESSONS_KEY);
  } catch {
    /* ignore — clearing is best-effort */
  }
}
